import type { ParsedQs } from 'qs'

import { getActiveChainId } from '@/config/active'
import { isNativeCurrencySymbol, nativeCurrencyId } from '@/config/native'
import { isAddress } from '@/utils'
import type { ChainId } from '@/sdk'

export type SwapEntryQuery =
  | { kind: 'none' }
  | { kind: 'poolId'; poolId: string }
  | { kind: 'pair'; pairAddress: string }
  | { kind: 'legs'; token0Id: string; token1Id: string }
  | { kind: 'swapLegs'; inputCurrencyId: string; outputCurrencyId: string }
  | { kind: 'outputOnly'; outputCurrencyId: string }

export type SwapCanonicalRef =
  | { type: 'poolId'; poolId: string }
  | { type: 'pair'; pairAddress: string }

export function parseSwapLegParam(urlParam: unknown, chainId: ChainId): string | null {
  if (typeof urlParam !== 'string' || !urlParam.trim()) return null
  const trimmed = urlParam.trim()
  const valid = isAddress(trimmed)
  if (valid) return valid
  if (isNativeCurrencySymbol(trimmed, chainId)) return nativeCurrencyId(chainId)
  if (trimmed.startsWith('0x')) return null
  return null
}

export function parseSwapEntryQuery(parsedQs: ParsedQs, chainId?: ChainId): SwapEntryQuery {
  const cid = chainId ?? getActiveChainId()

  if (typeof parsedQs.poolId === 'string' && parsedQs.poolId.trim()) {
    return { kind: 'poolId', poolId: parsedQs.poolId.trim() }
  }

  if (typeof parsedQs.pair === 'string' && parsedQs.pair.trim()) {
    const pairAddress = isAddress(parsedQs.pair.trim())
    if (!pairAddress) return { kind: 'none' }
    return { kind: 'pair', pairAddress }
  }

  if (typeof parsedQs.token0 === 'string' && typeof parsedQs.token1 === 'string') {
    const token0Id = parseSwapLegParam(parsedQs.token0, cid)
    const token1Id = parseSwapLegParam(parsedQs.token1, cid)
    if (token0Id && token1Id && token0Id !== token1Id) {
      return { kind: 'legs', token0Id, token1Id }
    }
  }

  const hasInput = typeof parsedQs.inputCurrency === 'string'
  const hasOutput = typeof parsedQs.outputCurrency === 'string'
  if (hasInput && hasOutput) {
    const inputCurrencyId = parseSwapLegParam(parsedQs.inputCurrency, cid)
    const outputCurrencyId = parseSwapLegParam(parsedQs.outputCurrency, cid)
    if (!inputCurrencyId || !outputCurrencyId) return { kind: 'none' }
    if (inputCurrencyId === outputCurrencyId) return { kind: 'none' }
    return { kind: 'swapLegs', inputCurrencyId, outputCurrencyId }
  }

  if (hasOutput && !hasInput) {
    const outputCurrencyId = parseSwapLegParam(parsedQs.outputCurrency, cid)
    if (!outputCurrencyId) return { kind: 'none' }
    return { kind: 'outputOnly', outputCurrencyId }
  }

  return { kind: 'none' }
}

export function hasSwapEntryParams(parsedQs: ParsedQs): boolean {
  return parseSwapEntryQuery(parsedQs).kind !== 'none'
}

export function buildCanonicalSwapSearch(ref: SwapCanonicalRef): string {
  const params = new URLSearchParams()
  if (ref.type === 'poolId') {
    params.set('poolId', ref.poolId)
  } else {
    params.set('pair', ref.pairAddress)
  }
  return `?${params.toString()}`
}

export function buildSwapEntryUrl(ref: SwapCanonicalRef): string {
  return `/swap${buildCanonicalSwapSearch(ref)}`
}
